import { useState } from 'react';
import { Calendar, MapPin, ExternalLink, CalendarPlus, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

// Calendar event data for each event
const eventCalendarData = {
  gala: {
    title: 'Aim Hire Annual Gala 2026',
    description: 'Join us for an elegant evening celebrating career success stories and the impact of meaningful employment. Support our mission while connecting with community leaders and partners.',
    location: 'Lavan Midtown, 641 W 42nd St, New York, NY 10036',
    startDate: '2026-10-26T18:00:00',
    endDate: '2026-10-26T22:00:00',
  },
};

// Generate calendar URLs
const generateGoogleCalendarUrl = (event) => {
  const startDate = event.startDate.replace(/[-:]/g, '').replace('T', 'T');
  const endDate = event.endDate.replace(/[-:]/g, '').replace('T', 'T');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${startDate}/${endDate}`,
    details: event.description,
    location: event.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const generateOutlookCalendarUrl = (event) => {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: event.startDate,
    enddt: event.endDate,
    body: event.description,
    location: event.location,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const generateICSFile = (event, filename) => {
  const formatDate = (dateStr) => {
    return dateStr.replace(/[-:]/g, '').replace('T', 'T') + '00';
  };
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Aim Hire//Event//EN
BEGIN:VEVENT
DTSTART:${formatDate(event.startDate)}
DTEND:${formatDate(event.endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.description.replace(/\n/g, '\\n')}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;
  
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Add to Calendar Dropdown Component
const AddToCalendarDropdown = ({ calendarEvent, filename }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-900 border border-slate-200 rounded-full px-6 py-3 text-base font-medium flex items-center justify-center gap-2 transition-colors duration-300"
        data-testid="add-to-calendar-btn"
      >
        <CalendarPlus className="w-4 h-4" />
        Add to Calendar
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-10"
          data-testid="calendar-dropdown"
        >
          <a
            href={generateGoogleCalendarUrl(calendarEvent)}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
            onClick={() => setIsOpen(false)}
            data-testid="google-calendar-link"
          >
            Google Calendar
          </a>
          <a
            href={generateOutlookCalendarUrl(calendarEvent)}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
            onClick={() => setIsOpen(false)}
            data-testid="outlook-calendar-link"
          >
            Outlook Calendar
          </a>
          <button
            onClick={() => {
              generateICSFile(calendarEvent, filename);
              setIsOpen(false);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="apple-calendar-link"
          >
            Apple Calendar (.ics)
          </button>
        </div>
      )}
    </div>
  );
};

// Events data - easily extendable for future events
const upcomingEvents = [
  {
    id: 1,
    title: 'Aim Hire Annual Gala 2026',
    date: 'Monday, October 26th, 2026',
    time: '6:00 PM Cocktail Hour | 7:00 PM Reception',
    location: 'Lavan Midtown, 641 W 42nd St, New York, NY 10036',
    description: 'Join us for an elegant evening celebrating career success stories and the impact of meaningful employment. Support our mission while connecting with community leaders and partners.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_7cd2d42f-ea20-476b-8611-c27f9b8d7057/artifacts/ui76ej62_Zeffy%20Image%20%281%29.jpg',
    registrationUrl: 'https://www.zeffy.com/en-US/ticketing/aim-hire-gala--2026',
    calendarKey: 'gala',
    icsFilename: 'aim-hire-gala-2026.ics',
  },
];

// Event Card Component
const EventCard = ({ event }) => {
  const calendarEvent = eventCalendarData[event.calendarKey];
  
  return (
    <div 
      className="bg-white rounded-xl overflow-hidden border border-slate-100 card-hover"
      data-testid={`event-card-${event.id}`}
    >
      <div className="grid lg:grid-cols-2">
        {/* Event Image */}
        <div className="relative aspect-square lg:aspect-auto lg:min-h-[400px]">
          <img 
            src={event.image} 
            alt={event.title}
            className="w-full h-full object-cover"
            data-testid={`event-image-${event.id}`}
          />
        </div>

        {/* Event Details */}
        <div className="p-6 lg:p-8 flex flex-col justify-center">
          <h3 
            className="text-xl lg:text-2xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {event.title}
          </h3>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-slate-600">
              <Calendar className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              <span className="text-sm">{event.date}</span>
            </div>
            {event.time && (
              <div className="flex items-center text-slate-600">
                <span className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="text-sm italic text-slate-500">{event.time}</span>
              </div>
            )}
            <div className="flex items-start text-slate-600">
              <MapPin className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{event.location}</span>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            {event.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a 
              href={event.registrationUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid={`event-register-btn-${event.id}`}
            >
              <Button className="w-full sm:w-auto bg-slate-900 hover:bg-purple-600 text-white rounded-full px-8 py-3 text-base font-medium flex items-center justify-center gap-2 transition-colors duration-300">
                Register Now
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
            <AddToCalendarDropdown calendarEvent={calendarEvent} filename={event.icsFilename} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Events() {
  return (
    <div data-testid="events-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="events-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              <span className="text-purple-200 italic">Upcoming Events</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Join us at our upcoming events to celebrate career success, connect with partners, and support our mission of meaningful employment.
            </p>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="events-grid">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          {upcomingEvents.length > 0 ? (
            <div className="space-y-8">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
              <p className="text-slate-600">No upcoming events at this time. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="events-cta">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="bg-slate-900 rounded-xl p-8 md:p-12 text-center">
            <h2 
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Want to Partner With Us?
            </h2>
            <p className="text-base text-slate-300 mb-6 max-w-2xl mx-auto">
              Interested in sponsoring an event or partnering with Aim Hire? We'd love to hear from you.
            </p>
            <a href="/contact" data-testid="events-contact-btn">
              <Button className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 py-6 text-lg font-medium">
                Get in Touch
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
